import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {addQueryToPath} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {CancelToken} from '../../helpers/asyncCancelHelpers';
import {makeMapStateToProps} from '../../helpers/reselectHelpers';
import {CustomFieldResourceTypesEnum} from '../../models/api/customFieldModel';
import {postAsync} from '../../services/http/http';
import {HttpError} from '../../services/http/httpError';
import {AppState} from '../../store/appState';
import {getCompany} from '../../store/data/company/companySelectors';
import {getTeamsByNamespace} from '../../store/data/teams/teamsSelectors';
import {AllowedCustomFieldResourceImportType} from './allowedCustomFieldsResourceImportTypeHelper';
import {SettingsCustomFieldAttributeImportCsv} from './settingsCustomFieldAttributeImportCsv';
import {
  DownloadTemplateLink,
  DownloadTemplateLinkTypesEnum,
} from './settingsCustomFieldAttributeImportCsvTypes';

/*
 * Props.
 */

interface SettingsCustomFieldAttributeImportCsvContainerProps {
  resourceType: AllowedCustomFieldResourceImportType;
  namespace: string;
}

/*
 * Selectors.
 */

function getResourceType(_state: AppState, props: SettingsCustomFieldAttributeImportCsvContainerProps) {
  return props.resourceType;
}

function getNamespace(_state: AppState, props: SettingsCustomFieldAttributeImportCsvContainerProps) {
  return props.namespace;
}

function makeGetUploadUrl() {
  return createSelector([getCompany, getResourceType], (company, resourceType) => {
    switch (resourceType) {
      case CustomFieldResourceTypesEnum.TEAMMATE:
        return `${company.url}/teammates/csv`;

      case CustomFieldResourceTypesEnum.INBOX:
        return `${company.url}/inbox_csv`;

      default:
        throw new UnreachableError(resourceType);
    }
  });
}

function makeGetUploadFileAsync(getUploadUrl: ReturnType<typeof makeGetUploadUrl>) {
  return createSelector(
    [getUploadUrl],
    (uploadUrl) => async (file: File, dryRun: boolean, cancelToken: CancelToken) => {
      try {
        const url = addQueryToPath(uploadUrl, {
          dry_run: dryRun,
        });

        const formData = new FormData();
        formData.append('file', file);
        await postAsync(url, formData, {cancelToken});
      } catch (error) {
        if (!(error instanceof HttpError)) {
          reportToBugsnag(error);
        }

        throw error;
      }
    },
  );
}

function makeGetRequiredColumns() {
  return createSelector(getResourceType, (resourceType) => {
    switch (resourceType) {
      case CustomFieldResourceTypesEnum.TEAMMATE:
        return ['teammate_login_email'];

      case CustomFieldResourceTypesEnum.INBOX:
        return ['inbox_id'];

      default:
        throw new UnreachableError(resourceType);
    }
  });
}

function makeGetDownloadTemplateCsvLink() {
  return createSelector(
    [getResourceType, getCompany, getTeamsByNamespace, getNamespace],
    (resourceType, company, teams, namespace): DownloadTemplateLink => {
      const team = teams[namespace];
      const baseApiUrl = `${company.url}/teams/${team.id}`;

      switch (resourceType) {
        case CustomFieldResourceTypesEnum.TEAMMATE:
          return {
            type: DownloadTemplateLinkTypesEnum.FILE,
            url: `${baseApiUrl}/teammate_csv_download`,
            name: 'Front Teammate Template.csv',
          };

        case CustomFieldResourceTypesEnum.INBOX:
          return {
            type: DownloadTemplateLinkTypesEnum.FILE,
            url: `${baseApiUrl}/inbox_csv_download`,
            name: 'Front Inbox Template.csv',
          };

        default:
          throw new UnreachableError(resourceType);
      }
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsCustomFieldAttributeImportCsvContainerProps>()(() => {
  const getUploadUrl = makeGetUploadUrl();

  return {
    requiredColumns: makeGetRequiredColumns(),
    downloadTemplateCsvLink: makeGetDownloadTemplateCsvLink(),
    uploadFileAsync: makeGetUploadFileAsync(getUploadUrl),
  };
});

export const SettingsCustomFieldAttributeImportCsvContainer = connect(mapStateToProps)(
  SettingsCustomFieldAttributeImportCsv,
);
